<template>
    <el-dialog
        class="eligibility-dialog"
        destroy-on-close
        show-close
        width="70%"
        :visible.sync="visible"
    >
        <template #title>
            <div class="eligibility-dialog__header">
                <span class="eligibility-dialog__title">
                    {{
                        $t(
                            'contrato_rastreabilidade.words.eligibility_analysis'
                        )
                    }}:
                </span>
                <div class="eligibility-dialog__status">
                    <i
                        :class="data.eligibility.verdict ? 'flow flow-verified eligible' : 'flow flow-warning-sign not-eligible'"
                    />
                    <span v-if="data.eligibility.verdict" class="eligible">
                        {{ $t('contrato_rastreabilidade.words.eligible') }}
                    </span>
                    <span v-else class="not-eligible">
                        {{ $t('contrato_rastreabilidade.words.not_eligible') }}
                    </span>
                </div>
            </div>
        </template>
        <div class="eligibility-dialog__content">
            <div class="eligibility-dialog__content__info">
                <div style="margin-top: 16px">
                    <h3>
                        <i
                            :class="data.eligibility.verdict ? 'flow flow-verified eligible' : 'flow flow-warning-sign not-eligible'"
                        />
                        {{
                            $t(
                                'contrato_rastreabilidade.words.socio_environmental_assessment'
                            )
                        }}
                    </h3>
                    <el-card shadow="never" class="eligibility-dialog__card">
                        <div class="eligibility-dialog__card__header">
                            <span>{{ translate('farm_name') }}:</span>
                            <span>{{ data.name }}</span>
                        </div>
                        <div class="eligibility-dialog__card__data">
                            <div>
                                <span>{{ translate('department') }}:</span>
                                <span>{{ data.department || 'N/D' }}</span>
                            </div>
                            <div>
                                <span>{{ translate('district') }}:</span>
                                <span>{{ data.district || 'N/D' }}</span>
                            </div>
                            <div>
                                <span>{{ translate('property_area') }}:</span>
                                <span
                                    >{{ Number(data.area).toFixed(2) }} ha</span
                                >
                            </div>
                        </div>
                        <el-divider />
                        <span
                            :class="data.eligibility.verdict ? 'eligible' : 'not-eligible'"
                        >
                            {{
                                translate(
                                    data.eligibility.verdict
                                        ? 'conformity_message'
                                        : 'non_conformity_message'
                                )
                            }}
                        </span>
                        <div v-if="!data.eligibility.verdict">
                            <el-divider/>
                            <div
                                v-for="(item, index) in data.eligibility.details
                                    .geometry"
                                :key="index"
                            >
                                <p>
                                    {{ $t(`mensagens.camadas.${item.camada.codigo}`)  }}
                                    <i
                                        :class="item.possuiRestricao ? 'flow flow-warning-sign not-eligible' : 'flow flow-verified eligible'"
                                    />
                                </p>
                            </div>
                        </div>
                    </el-card>
                </div>
                <div style="margin-top: 16px">
                    <el-card shadow="never" class="eligibility-dialog__card">
                        <h3>{{ translate('lyra_code') }}: {{ data.producer_lyra_code }}</h3>
                        <el-divider />
                        <span
                            :class="data.eligibility.verdict ? 'eligible' : 'not-eligible'"
                        >
                            {{
                                translate(
                                    data.eligibility.verdict
                                        ? 'conformity_message'
                                        : 'non_conformity_message'
                                )
                            }}
                        </span>
                    </el-card>
                </div>
            </div>
            <div class="eligibility-dialog__content__map">
                <mapa
                    ref="mapaEligibility"
                    @is-ready='fitMapBounds'
                    :mapLayersConfigs="layersMaps"
                    :mapConfig="mapConfig"
                    :atualizarMapa="true"
                    idMapa="mapa-eligibility-dialog"
                    height="100%"
                    locked
                    v-if="showMap"
                />
            </div>
        </div>
        <div class="eligibility-dialog__actions">
            <el-button
                type="primary"
                @click="visible = false"
                size="small"
                style="margin-top: 16px"
            >
                {{ $t('contrato_rastreabilidade.words.close') }}
            </el-button>
        </div>
    </el-dialog>
</template>

<script>
import { getEstiloGeometria } from '@/utils/mapa/mapa.estilos';
import Mapa from '@/components/MapaLeaflet.vue';

export default {
    name: 'DialogEligibilityAnalysis',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        data: {
            type: Object,
            default: () => ({})
        }
    },
    components: {
        Mapa
    },
    data() {
        return {
            layersMaps: [],
            mapConfig: {
                controls: {}
            },
            showMap: null
        };
    },
    mounted() {
        this.layersMaps.push({
            geoJson: this.data.eligibility.geometry,
            style: getEstiloGeometria('area')
        });
        this.showMap = true;
    },
    computed: {
        visible: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        }
    },
    methods: {
        translate(key) {
            const base =
                'contrato_rastreabilidade.messages.eligibility_dialog';
            return this.$t(`${base}.${key}`);
        },
        fitMapBounds(mapa) {
            setTimeout(() => {
                const bounds = L.geoJSON(this.data.eligibility.geometry).getBounds();
                mapa.fitBounds(bounds);
            }, 400);
        }
    }
};
</script>

<style scoped lang="scss">
.eligibility-dialog {
    .eligibility-dialog__header {
        display: flex;
        gap: 8px;
        align-items: center;

        .eligibility-dialog__title {
            font-weight: 400;
            font-size: 16px;
            color: #3b3b3b;
        }

        .eligibility-dialog__status {
            font-weight: 500;
        }

        .el-icon-circle-check {
            font-weight: bold;
        }
    }

    .eligibility-dialog__content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 18px;

        .eligibility-dialog__content__info {
            height: 400px;
            overflow: auto;
        }

        .eligibility-dialog__card {
            background-color: #fafafa;
            border: 1px solid #e7e7e7;

            .eligibility-dialog__card__header {
                display: flex;
                gap: 8px;

                span:last-child {
                    font-weight: 500;
                    color: #00413c;
                }
            }

            .eligibility-dialog__card__data {
                margin-top: 16px;
                display: flex;
                justify-content: space-between;
                color: #3b3b3b;

                > div {
                    display: flex;
                    flex-direction: column;

                    span:last-child {
                        font-weight: 500;
                        color: #00413c;
                    }
                }
            }
        }
    }

    .eligibility-dialog__actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
    }

    .eligible {
        color: #359138;
    }

    .not-eligible {
        color: #d9534f;
    }
}
</style>
